import React, { useState } from "react";
import { Shirt, Award, Medal as MedalIcon, Loader2, Send, CheckCircle2, Palette, Type, ImagePlus, Move } from "lucide-react";
import KitPreview from "../components/customizer/KitPreview";
import TshirtDesigner from "../components/customizer/TshirtDesigner";
import MedalPreview from "../components/customizer/MedalPreview";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import { uploadDesignImage, submitQuote } from "../services/api";

const PALETTE = ["#C81E1E", "#1E40AF", "#047857", "#0F172A", "#F59E0B", "#DB2777", "#7C3AED", "#0EA5E9", "#FFFFFF", "#111827", "#C9A227", "#94A3B8"];

const teeSide = (text, ty, tsize) => ({ logo: "", logoX: 160, logoY: 150, logoSize: 80, text, textX: 160, textY: ty, textSize: tsize, textColor: "#FFFFFF" });

const defaults = {
  "football-kit": { pattern: "stripes", primary: "#C81E1E", secondary: "#FFFFFF", accent: "#0F172A", teamName: "UNITED", number: "10", logo: "" },
  tshirt: { color: "#1E40AF", secondary: "#FFFFFF", pattern: "solid", view: "front", front: teeSide("TEAM 2026", 250, 26), back: teeSide("10", 180, 70) },
  medal: { shape: "circle", finish: "gold", ribbon: "#C81E1E", text: "CHAMPION", logo: "" },
};

const TYPES = [
  { key: "football-kit", label: "Football Kit", icon: Award },
  { key: "tshirt", label: "T-Shirt + Shorts", icon: Shirt },
  { key: "medal", label: "Medal", icon: MedalIcon },
];

const Swatches = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {PALETTE.map((c) => (
      <button key={c} type="button" onClick={() => onChange(c)}
        className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${value === c ? "border-[#C9A227] ring-2 ring-[#C9A227]/30" : "border-neutral-200"}`}
        style={{ backgroundColor: c }} />
    ))}
    <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-7 h-7 rounded-full border border-neutral-200 cursor-pointer p-0" />
  </div>
);

const Field = ({ label, icon: Icon, children }) => (
  <div>
    <div className="flex items-center gap-1.5 text-sm font-semibold text-neutral-700 mb-2">{Icon && <Icon className="w-4 h-4 text-[#C9A227]" />}{label}</div>
    {children}
  </div>
);

const Pills = ({ options, value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((p) => (
      <button key={p} onClick={() => onChange(p)} className={`px-3 py-1.5 rounded-full text-sm capitalize border ${value === p ? "bg-[#14171c] text-white border-[#14171c]" : "border-neutral-200 text-neutral-600 hover:border-[#C9A227]"}`}>{p}</button>
    ))}
  </div>
);

const rasterize = (svgEl) =>
  new Promise((resolve, reject) => {
    try {
      const xml = new XMLSerializer().serializeToString(svgEl);
      const svg64 = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = svgEl.viewBox.baseVal.width * 2;
        canvas.height = svgEl.viewBox.baseVal.height * 2;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = svg64;
    } catch (e) { reject(e); }
  });

const Customizer = () => {
  const [type, setType] = useState("football-kit");
  const [designs, setDesigns] = useState(defaults);
  const [form, setForm] = useState({ name: "", email: "", quantity: "", message: "" });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const design = designs[type];
  const set = (k, v) => setDesigns((d) => ({ ...d, [type]: { ...d[type], [k]: v } }));
  const view = design.view;
  const sideData = type === "tshirt" ? design[view] : null;
  const setSide = (field, val) => set(view, { ...design[view], [field]: val });

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => (type === "tshirt" ? setSide("logo", reader.result) : set("logo", reader.result));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast({ title: "Name and email required", variant: "destructive" }); return; }
    setSending(true);
    try {
      let designImage, designMeta;
      if (type === "tshirt") {
        const f = await rasterize(document.getElementById("ts-front"));
        const b = await rasterize(document.getElementById("ts-back"));
        const [upF, upB] = await Promise.all([uploadDesignImage(f), uploadDesignImage(b)]);
        designImage = upF.url;
        const strip = (o) => { const { logo, ...r } = o; return { ...r, hasLogo: !!logo }; };
        designMeta = { type: "tshirt-set", color: design.color, secondary: design.secondary, pattern: design.pattern, front: strip(design.front), back: strip(design.back), backImage: upB.url };
      } else {
        const dataUrl = await rasterize(document.getElementById("design-svg"));
        const up = await uploadDesignImage(dataUrl);
        designImage = up.url;
        const { logo, ...rest } = design;
        designMeta = { type, ...rest, hasLogo: !!logo };
      }
      await submitQuote({
        name: form.name, email: form.email, quantity: form.quantity,
        message: form.message || `Custom ${TYPES.find((t) => t.key === type).label} design request.`,
        productCategory: TYPES.find((t) => t.key === type).label,
        design: designMeta, designImage,
      });
      setSubmitted(true);
      toast({ title: "Design submitted!", description: "We'll send you a quote within 24 hours." });
    } catch (err) {
      toast({ title: "Submission failed", description: "Please try again.", variant: "destructive" });
    } finally { setSending(false); }
  };

  return (
    <div>
      <section className="bg-ink-gradient py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-[#E7C766] font-bold text-sm uppercase tracking-widest">Design Studio</span>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl text-white mt-3">Personalise Your Product</h1>
          <p className="text-neutral-300 mt-4 max-w-2xl mx-auto">Design your football kit, T-shirt or medal live, then send it to us for a factory-direct quote.</p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-center gap-3 mb-8 flex-wrap">
            {TYPES.map((t) => (
              <button key={t.key} onClick={() => { setType(t.key); setSubmitted(false); }}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm border transition-colors ${type === t.key ? "bg-[#C9A227] border-[#C9A227] text-white" : "bg-white border-neutral-200 text-neutral-700 hover:border-[#C9A227]"}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Preview */}
            <div className="bg-white rounded-2xl border border-neutral-100 p-6 sticky top-24 self-start">
              {type === "tshirt" && (
                <div className="flex justify-center gap-2 mb-4">
                  {["front", "back"].map((v) => (
                    <button key={v} onClick={() => set("view", v)} className={`px-4 py-1.5 rounded-full text-sm font-semibold capitalize border ${view === v ? "bg-[#14171c] text-white border-[#14171c]" : "border-neutral-200 text-neutral-600"}`}>{v} view</button>
                  ))}
                </div>
              )}
              <div className="max-w-sm w-full mx-auto">
                {type === "football-kit" && <KitPreview design={design} />}
                {type === "medal" && <MedalPreview design={design} />}
                {type === "tshirt" && <TshirtDesigner design={design} side={view} svgId="ts-view" interactive onChange={setSide} />}
              </div>
              {type === "tshirt" && (
                <p className="text-center text-xs text-neutral-500 mt-3 flex items-center justify-center gap-1"><Move className="w-3.5 h-3.5" /> Drag the logo or text on the shirt to reposition</p>
              )}
              {/* hidden front+back for rasterization */}
              {type === "tshirt" && (
                <div style={{ position: "absolute", left: -99999, top: 0, width: 1, height: 1, overflow: "hidden" }} aria-hidden>
                  <TshirtDesigner design={design} side="front" svgId="ts-front" />
                  <TshirtDesigner design={design} side="back" svgId="ts-back" />
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="bg-white rounded-2xl border border-neutral-100 p-6">
              {submitted ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-16 h-16 text-[#C9A227] mx-auto mb-4" />
                  <h3 className="font-display font-extrabold text-2xl text-[#14171c]">Design Received!</h3>
                  <p className="text-neutral-600 mt-2">Our team will review your design and reply with a quote within 24 hours.</p>
                  <Button onClick={() => setSubmitted(false)} className="mt-6 bg-[#C9A227] hover:bg-[#A6841B] text-white rounded-full px-7">Design Another</Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {type === "football-kit" && (
                    <>
                      <Field label="Pattern" icon={Palette}><Pills options={["solid", "stripes", "hoops", "halves", "sash"]} value={design.pattern} onChange={(v) => set("pattern", v)} /></Field>
                      <Field label="Primary Colour"><Swatches value={design.primary} onChange={(v) => set("primary", v)} /></Field>
                      <Field label="Secondary Colour"><Swatches value={design.secondary} onChange={(v) => set("secondary", v)} /></Field>
                      <Field label="Accent (text/trim)"><Swatches value={design.accent} onChange={(v) => set("accent", v)} /></Field>
                      <div className="grid grid-cols-2 gap-4">
                        <Field label="Team Name" icon={Type}><Input value={design.teamName} maxLength={14} onChange={(e) => set("teamName", e.target.value)} /></Field>
                        <Field label="Number"><Input value={design.number} maxLength={2} onChange={(e) => set("number", e.target.value.replace(/[^0-9]/g, ""))} /></Field>
                      </div>
                    </>
                  )}

                  {type === "tshirt" && (
                    <>
                      <Field label="Pattern" icon={Palette}><Pills options={["solid", "stripes", "raglan"]} value={design.pattern} onChange={(v) => set("pattern", v)} /></Field>
                      <Field label="Shirt Colour"><Swatches value={design.color} onChange={(v) => set("color", v)} /></Field>
                      <Field label="Accent / Stripe Colour"><Swatches value={design.secondary} onChange={(v) => set("secondary", v)} /></Field>

                      <div className="rounded-xl bg-neutral-50 p-4 space-y-4">
                        <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227]">Editing: {view} side</div>
                        <Field label="Print Text" icon={Type}><Input value={sideData.text} maxLength={16} onChange={(e) => setSide("text", e.target.value)} /></Field>
                        <div className="grid grid-cols-2 gap-4">
                          <Field label="Text Colour"><Swatches value={sideData.textColor} onChange={(v) => setSide("textColor", v)} /></Field>
                          <Field label={`Text Size (${sideData.textSize})`}>
                            <input type="range" min="14" max="90" value={sideData.textSize} onChange={(e) => setSide("textSize", Number(e.target.value))} className="w-full accent-[#C9A227]" />
                          </Field>
                        </div>
                        <Field label="Upload Logo / Crest" icon={ImagePlus}>
                          <div className="flex items-center gap-3">
                            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-neutral-300 cursor-pointer hover:border-[#C9A227] text-sm text-neutral-600">
                              <ImagePlus className="w-4 h-4 text-[#C9A227]" /> Choose image
                              <input type="file" accept="image/*" className="hidden" onChange={onLogo} />
                            </label>
                            {sideData.logo && <button onClick={() => setSide("logo", "")} className="text-sm text-red-500">Remove</button>}
                          </div>
                        </Field>
                        {sideData.logo && (
                          <Field label={`Logo Size (${sideData.logoSize})`}>
                            <input type="range" min="30" max="180" value={sideData.logoSize} onChange={(e) => setSide("logoSize", Number(e.target.value))} className="w-full accent-[#C9A227]" />
                          </Field>
                        )}
                      </div>
                    </>
                  )}

                  {type === "medal" && (
                    <>
                      <Field label="Shape" icon={Palette}><Pills options={["circle", "star", "shield"]} value={design.shape} onChange={(v) => set("shape", v)} /></Field>
                      <Field label="Finish"><Pills options={["gold", "silver", "bronze", "antique"]} value={design.finish} onChange={(v) => set("finish", v)} /></Field>
                      <Field label="Ribbon Colour"><Swatches value={design.ribbon} onChange={(v) => set("ribbon", v)} /></Field>
                      <Field label="Engraved Text" icon={Type}><Input value={design.text} maxLength={18} onChange={(e) => set("text", e.target.value)} /></Field>
                      <Field label="Upload Centre Logo" icon={ImagePlus}>
                        <div className="flex items-center gap-3">
                          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-neutral-300 cursor-pointer hover:border-[#C9A227] text-sm text-neutral-600">
                            <ImagePlus className="w-4 h-4 text-[#C9A227]" /> Choose image
                            <input type="file" accept="image/*" className="hidden" onChange={onLogo} />
                          </label>
                          {design.logo && <button onClick={() => set("logo", "")} className="text-sm text-red-500">Remove</button>}
                        </div>
                      </Field>
                    </>
                  )}

                  <div className="border-t border-neutral-100 pt-6">
                    <h3 className="font-display font-bold text-lg text-[#14171c] mb-4">Get a Quote for This Design</h3>
                    <form onSubmit={submit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Name *</Label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                        <div><Label>Email *</Label><Input type="email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                      </div>
                      <div><Label>Quantity</Label><Input className="mt-1.5" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="e.g. 25 sets" /></div>
                      <div><Label>Notes</Label><Textarea className="mt-1.5" rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Sizes, colours, deadline..." /></div>
                      <Button type="submit" disabled={sending} className="w-full bg-[#C9A227] hover:bg-[#A6841B] text-white rounded-full h-12 font-semibold">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Submit Design & Request Quote
                      </Button>
                    </form>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Customizer;
